/**
 * RadioNumberInput - A custom widget for JSON Schema forms that displays radio buttons
 * with optional number pickers aligned to the right of specific options.
 *
 * Usage in uiSchema:
 * {
 *   fieldName: {
 *     'ui:widget': 'RadioNumberInput',
 *     'ui:placeholder': 'Select an option',
 *     'ui:label': false, // Optional: set to false to hide the field label
 *     'ui:size': 'small', // Optional: 'small' | 'medium' (default) | 'large'
 *     'ui:options': [
 *       {
 *         text: 'Option without number',
 *         value: 'option1',
 *         hasNumberPicker: false
 *       },
 *       {
 *         text: 'Option with number',
 *         value: 'option2',
 *         hasNumberPicker: true,
 *         numberRange: { min: 1, max: 100 }
 *       }
 *     ]
 *   }
 * }
 *
 * Value format: { option: string, count?: number }
 */

import React, { useState, useMemo, useEffect } from 'react';
import { WidgetProps } from '@rjsf/utils';
import { Radio, FormControlLabel, FormControl, TextField, Box, Tooltip } from '@mui/material';
import { INPUT_STYLE } from '../../../../../utils/styleHelpers';
import { DetailRow } from '../../../DetailsDrawer/DetailRow';

interface RadioOption {
  text: string;
  value: string;
  hasNumberPicker?: boolean;
  numberRange?: {
    min?: number;
    max?: number;
  };
}

interface RadioNumberValue {
  option: string;
  count?: number;
}

export const RadioNumberInput = (props: WidgetProps) => {
  const { value: formValue, onChange } = props;
  const [dirty, setDirty] = useState(false);

  // Parse the initial value - handle both string and object formats
  let initialValue: RadioNumberValue;
  if (typeof formValue === 'string' && formValue.trim() !== '') {
    // Try to parse as JSON if it's a non-empty string
    try {
      initialValue = JSON.parse(formValue);
    } catch {
      // If parsing fails, treat the string as option only
      initialValue = { option: formValue, count: undefined };
    }
  } else if (formValue && typeof formValue === 'object') {
    // If it's an object, use it directly (normal case, like CompoundTextInput)
    initialValue = formValue as RadioNumberValue;
  } else {
    // Default empty value
    initialValue = { option: '', count: undefined };
  }

  // Get options from uiSchema first
  let options: RadioOption[] = [];
  const rawOptions = props.uiSchema?.['ui:options'];

  if (Array.isArray(rawOptions)) {
    options = rawOptions as RadioOption[];
  } else if (rawOptions && typeof rawOptions === 'object') {
    // Convert object with numeric keys back to array
    options = Object.values(rawOptions) as RadioOption[];
  }

  const [selectedOption, setSelectedOption] = useState<string>(() => {
    return initialValue.option || (options.length > 0 ? options[0].value : '');
  });

  const [numberValue, setNumberValue] = useState<number | undefined>(() => {
    // If we have an initial number value, use it
    if (initialValue.count !== undefined) {
      return initialValue.count;
    }

    // If no initial number value but we're auto-selecting the first option with a number picker
    const selectedVal = initialValue.option || (options.length > 0 ? options[0].value : '');
    if (selectedVal && options.length > 0) {
      const selectedOptionConfig = options.find((opt) => opt.value === selectedVal);
      if (selectedOptionConfig?.hasNumberPicker) {
        return selectedOptionConfig.numberRange?.min || 1;
      }
    }

    return undefined;
  });

  // Calculate the maximum text width for consistent alignment
  const maxTextWidth = useMemo(() => {
    return Math.max(...options.map((option) => option.text.length)) * 8 + 60; // Approximate character width + padding
  }, [options]);

  // Auto-select first option if none selected and trigger default number value
  useEffect(() => {
    if (!selectedOption && options.length > 0) {
      const firstOption = options[0];
      setSelectedOption(firstOption.value);

      if (firstOption.hasNumberPicker) {
        const defaultValue = firstOption.numberRange?.min || 1;
        setNumberValue(defaultValue);
      } else {
        setNumberValue(undefined);
      }
    }
  }, [options, selectedOption]);

  // Trigger onChange when values are set (including initial values)
  useEffect(() => {
    if (selectedOption && options.length > 0) {
      const selectedOptionConfig = options.find((opt) => opt.value === selectedOption);

      const callOnChange = () => {
        // Check for validation errors
        if (hasValidationErrors()) {
          onChange(null); // Report error to RJSF
        } else if (selectedOptionConfig?.hasNumberPicker && numberValue !== undefined) {
          onChange({
            option: selectedOption,
            count: numberValue
          });
        } else if (!selectedOptionConfig?.hasNumberPicker) {
          onChange({ option: selectedOption });
        }
      };

      // Add a small delay to ensure RJSF is ready to handle the onChange
      setTimeout(callOnChange, 0);
    }
  }, [selectedOption, numberValue, options]);

  // Additional initialization effect to ensure initial values are reported
  useEffect(() => {
    if (options.length > 0 && selectedOption) {
      const selectedOptionConfig = options.find((opt) => opt.value === selectedOption);
      if (selectedOptionConfig) {
        const callOnChange = () => {
          // Check for validation errors
          if (hasValidationErrors()) {
            onChange(null); // Report error to RJSF
          } else if (selectedOptionConfig.hasNumberPicker && numberValue !== undefined) {
            onChange({
              option: selectedOption,
              count: numberValue
            });
          } else if (!selectedOptionConfig.hasNumberPicker) {
            onChange({ option: selectedOption });
          }
        };

        // Ensure initial value is reported even if other effects don't trigger
        setTimeout(callOnChange, 10);
      }
    }
  }, [options]); // Only depend on options to run when they become available
  if (!options || options.length === 0) {
    return (
      <div style={{ padding: '10px', border: '2px solid orange', backgroundColor: '#fff3cd' }}>
        <strong>RadioNumberInput:</strong> No options configured or options is not an array.
        <br />
        Raw options: {JSON.stringify(rawOptions)}
        <br />
        <small>Field: {props.name || props.id}</small>
      </div>
    );
  }

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!dirty) setDirty(true);
    const newOption = event.target.value;
    setSelectedOption(newOption);

    const selectedOptionConfig = options.find((opt) => opt.value === newOption);

    // If the new option doesn't have a number picker, clear the number value
    if (!selectedOptionConfig?.hasNumberPicker) {
      setNumberValue(undefined);
      onChange({ option: newOption });
    } else {
      // Set default to the minimum value if no current value or if current value is invalid
      const range = selectedOptionConfig.numberRange;
      let validNumberValue = numberValue;

      // If no existing value or existing value is out of range, default to minimum
      if (
        numberValue === undefined ||
        (range &&
          ((range.min !== undefined && numberValue < range.min) ||
            (range.max !== undefined && numberValue > range.max)))
      ) {
        validNumberValue = range?.min || 1; // Default to min value or 1 if no min specified
      }

      setNumberValue(validNumberValue);
      onChange({
        option: newOption,
        count: validNumberValue
      });
    }
  };

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!dirty) setDirty(true);
    const newValue = event.target.value === '' ? undefined : Number(event.target.value);
    setNumberValue(newValue);

    // Check for validation errors with the new value
    const selectedConfig = getSelectedOptionConfig();
    let hasErrors = false;

    if (selectedConfig?.hasNumberPicker) {
      if (newValue === undefined) {
        hasErrors = true; // Required number field is empty
      } else {
        const range = selectedConfig.numberRange;
        if (range) {
          if ((range.min !== undefined && newValue < range.min) || (range.max !== undefined && newValue > range.max)) {
            hasErrors = true; // Number is out of range
          }
        }
      }
    }

    if (hasErrors) {
      onChange(null); // Report error to RJSF
    } else {
      onChange({
        option: selectedOption,
        count: newValue
      });
    }
  };

  const getSelectedOptionConfig = () => {
    return options.find((opt) => opt.value === selectedOption);
  };

  const isNumberPickerEnabled = () => {
    const selectedConfig = getSelectedOptionConfig();
    return selectedOption && selectedConfig?.hasNumberPicker;
  };

  const getNumberPickerProps = () => {
    const selectedConfig = getSelectedOptionConfig();
    const range = selectedConfig?.numberRange || {};

    return {
      type: 'number',
      inputProps: {
        min: range.min,
        max: range.max
      }
    };
  };

  const getHelperText = () => {
    if (isErrorState()) {
      if (!selectedOption && props.required) return `${props.label || 'Selection'} required`;
      const selectedConfig = getSelectedOptionConfig();
      if (selectedConfig?.hasNumberPicker) {
        if (numberValue === undefined) {
          return 'Number value required';
        }
        const range = selectedConfig.numberRange;
        if (range) {
          if (range.min !== undefined && numberValue < range.min) {
            return `Number must be at least ${range.min}`;
          }
          if (range.max !== undefined && numberValue > range.max) {
            return `Number must be no more than ${range.max}`;
          }
        }
      }
    }
  };

  const isErrorState = () => {
    if (!dirty) return false;
    if (props.rawErrors && props.rawErrors.length > 0) return true;
    if (props.required) {
      if (!selectedOption) return true;
      const selectedConfig = getSelectedOptionConfig();
      if (selectedConfig?.hasNumberPicker) {
        // Check if number is required but missing
        if (numberValue === undefined) return true;
        // Check if number is out of range
        const range = selectedConfig.numberRange;
        if (range) {
          if (
            (range.min !== undefined && numberValue < range.min) ||
            (range.max !== undefined && numberValue > range.max)
          ) {
            return true;
          }
        }
      }
    }
    return false;
  };

  const hasValidationErrors = () => {
    if (!selectedOption) return props.required;
    const selectedConfig = getSelectedOptionConfig();
    if (selectedConfig?.hasNumberPicker) {
      // Check if number is required but missing
      if (numberValue === undefined) return true;
      // Check if number is out of range
      const range = selectedConfig.numberRange;
      if (range) {
        if (
          (range.min !== undefined && numberValue < range.min) ||
          (range.max !== undefined && numberValue > range.max)
        ) {
          return true;
        }
      }
    }
    return false;
  };

  const formatDisplayValue = (value: RadioNumberValue) => {
    if (!value?.option) return '';
    const selectedConfig = options.find((opt) => opt.value === value.option);
    const text = selectedConfig?.text || value.option;

    if (value.count !== undefined) {
      return `${text}: ${value.count}`;
    }
    return text;
  };

  if (props.readonly) {
    if (props.formContext?.hideEmpty && !formValue?.option) return null;

    return (
      <div>
        <DetailRow
          key={props.id}
          colId={props.id}
          title={props.uiSchema?.['ui:placeholder'] || ''}
          value={formatDisplayValue(formValue)}
        />
      </div>
    );
  }

  return (
    <Box width="100%">
      <FormControl
        component="fieldset"
        error={isErrorState()}
        fullWidth>
        <Box
          display="flex"
          flexDirection="column">
          {options.map((option) => (
            <Box
              key={option.value}
              display="flex"
              alignItems="center">
              <FormControlLabel
                value={option.value}
                control={
                  <Radio
                    checked={selectedOption === option.value}
                    onChange={handleRadioChange}
                    name={props.uiSchema?.['ui:placeholder']}
                    size={props.uiSchema?.['ui:size'] || 'small'}
                  />
                }
                label={option.text}
                sx={{
                  minWidth: `${maxTextWidth}px`,
                  marginRight: 2,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '.875rem'
                  }
                }}
              />
              <Box sx={{ width: '75px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                {option.hasNumberPicker && selectedOption === option.value && (
                  <Tooltip
                    title="Enter number"
                    arrow>
                    <TextField
                      {...getNumberPickerProps()}
                      value={numberValue || ''}
                      onChange={handleNumberChange}
                      disabled={!isNumberPickerEnabled()}
                      size={props.uiSchema?.['ui:size'] || 'small'}
                      error={isErrorState()}
                      InputProps={{
                        style: { fontSize: '.875rem' }
                      }}
                      className={INPUT_STYLE}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
        </Box>
        {getHelperText() && (
          <Box
            mt={1}
            fontSize="0.75rem"
            color="error.main">
            {getHelperText()}
          </Box>
        )}
      </FormControl>
    </Box>
  );
};

export default RadioNumberInput;
